import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useJwt } from "../lib/session";
import { supabase, isSupabaseEnabled } from "../lib/supabaseClient";
import { genericizeCredentialError } from "../lib/authErrors";
import { resolveClientRole, resolveVolunteerParishSlug } from "../lib/roleCheck";
import { DEFAULT_PARISH_SLUG } from "../lib/defaultParish";
import AuthScreenHeader from "../components/AuthScreenHeader";

// Renders at both /sign-in (legacy, unscoped) and /:parish/admin/login
// (the parish-scoped entry point new signup/login links use). Same
// form either way; :parish just supplies a slug for the "wrong
// portal" redirect below when the URL has one.
export default function SignIn() {
  const jwt = useJwt();
  if (!isSupabaseEnabled) {
    // No Supabase project provisioned yet — bounce back to the
    // stub-session dashboard so the dev environment keeps working.
    return <Navigate to="/" replace />;
  }
  if (jwt) {
    // Already signed in via the ambient scheduler SessionProvider
    // (only present when rendered at /sign-in — the parish-scoped
    // route has no such ancestor, so this simply never fires there,
    // which is fine: worst case an already-signed-in admin sees the
    // form again instead of an instant bounce).
    return <Navigate to="/" replace />;
  }
  return <SignInForm />;
}

function SignInForm() {
  const navigate = useNavigate();
  const { parish: slug } = useParams();
  const location = useLocation();
  // Set by VolunteerLogin.tsx when volunteer credentials land here
  // after a wrong-portal redirect (see submitPassword() below for the
  // reverse case).
  const incomingAuthError = (location.state as { authError?: string } | null)?.authError ?? null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<
    "idle" | "signing-in" | "sending-link" | "sent" | "error"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  // "Forgot password?" reveals an inline request form in place of the
  // sign-in form — same toggle pattern as the volunteer portal's
  // (VolunteerLogin.tsx). The emailed link lands on /reset-password.
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [resetError, setResetError] = useState<string | null>(null);

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setResetStatus("sending");
    setResetError(null);
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    // resetPasswordForEmail doesn't distinguish "no such user" from
    // success (enumeration protection), so an error here is a real
    // failure (network, rate limit). The confirmation copy stays
    // neutral either way.
    if (resetErr) {
      setResetStatus("error");
      setResetError(resetErr.message);
      return;
    }
    setResetStatus("sent");
  }

  function backToSignIn() {
    setShowForgot(false);
    setResetStatus("idle");
    setResetError(null);
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    setStatus("signing-in");
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) {
      setStatus("error");
      setError(genericizeCredentialError(signInError.message));
      return;
    }

    // Backend enforcement (worker/src/lib/roles.ts) is the real
    // security boundary regardless of what happens here — this is
    // just the fast, friendly redirect instead of leaving a volunteer
    // staring at an admin shell full of 403 errors.
    const role = data.user ? await resolveClientRole(data.user.id) : "none";
    if (role !== "admin") {
      // Send the volunteer to THEIR parish's login — the URL slug if
      // this is the parish-scoped route, else the parish their
      // volunteer_accounts row points at. The pilot slug is the last
      // resort only (unscoped /sign-in + unresolvable account).
      const accountSlug = data.user ? await resolveVolunteerParishSlug(data.user.id) : null;
      await supabase.auth.signOut();
      const targetSlug = slug ?? accountSlug ?? DEFAULT_PARISH_SLUG;
      navigate(`/${targetSlug}/login`, {
        state: {
          authError: "This login is for parish staff — volunteers sign in here.",
        },
      });
      return;
    }
    // Admin confirmed. At /sign-in the ambient SessionProvider's
    // onAuthStateChange re-renders into the dashboard on its own —
    // but at /:parish/admin/login there is no provider above this
    // component (see main.tsx AdminLoginRoute), so nothing reacts to
    // the new session and the form sat on "Signing in…" forever.
    // Navigate to the admin app root explicitly: the * catch-all
    // mounts AuthedShell, whose SessionProvider reads the stored
    // session on mount. Correct in both contexts; status stays
    // "signing-in" so the button is disabled during the transition.
    navigate("/", { replace: true });
  }

  async function sendMagicLink() {
    if (!supabase) return;
    if (!email.trim()) {
      setStatus("error");
      setError("Enter your email first, then send the link.");
      return;
    }
    setStatus("sending-link");
    setError(null);
    const { error: signInError } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (signInError) {
      setStatus("error");
      setError(signInError.message);
      return;
    }
    setStatus("sent");
  }

  if (status === "sent") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader title="Check your email" />
          <div className="sign-in__confirmation">
            <p>
              Check your inbox at <strong>{email}</strong> for the
              sign-in link. It'll expire in an hour.
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (showForgot) {
    if (resetStatus === "sent") {
      return (
        <main className="page page--sign-in">
          <div className="sign-in">
            <AuthScreenHeader title="Check your email" />
            <div className="sign-in__confirmation">
              <p>
                If an account exists for <strong>{resetEmail}</strong>, we've sent a link to
                reset your password.
              </p>
            </div>
            <div className="sign-in__alt">
              <button type="button" className="sign-in__link" onClick={backToSignIn}>
                Back to sign in
              </button>
            </div>
          </div>
        </main>
      );
    }

    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            title="Reset your password"
            subtitle="Enter your email and we'll send you a link to reset it."
          />

          <form className="sign-in__form" onSubmit={submitReset}>
            <label className="sign-in__field">
              <span className="sign-in__label">Email</span>
              <input
                type="email"
                className="sign-in__input"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </label>
            <button type="submit" className="btn" disabled={resetStatus === "sending"}>
              {resetStatus === "sending" ? "Sending…" : "Send reset link"}
            </button>
            {resetStatus === "error" ? <p className="sign-in__error">{resetError}</p> : null}
          </form>

          <div className="sign-in__alt">
            <button type="button" className="sign-in__link" onClick={backToSignIn}>
              Back to sign in
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          title="Staff sign in"
          subtitle="Sign in with your email and password, or send yourself a one-time link instead."
        />
        {incomingAuthError ? <p className="sign-in__error">{incomingAuthError}</p> : null}

        <form className="sign-in__form" onSubmit={submitPassword}>
          <label className="sign-in__field">
            <span className="sign-in__label">Email</span>
            <input
              type="email"
              className="sign-in__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@parish.example.com"
              autoComplete="email"
              required
              autoFocus
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Password</span>
            <input
              type="password"
              className="sign-in__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <button
            type="submit"
            className="btn"
            disabled={status === "signing-in" || status === "sending-link"}
          >
            {status === "signing-in" ? "Signing in…" : "Sign in"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
        </form>

        <div className="sign-in__alt">
          <button
            type="button"
            className="sign-in__link"
            onClick={sendMagicLink}
            disabled={status === "signing-in" || status === "sending-link"}
          >
            {status === "sending-link"
              ? "Sending sign-in link…"
              : "Send sign-in link instead"}
          </button>
        </div>
        <div className="sign-in__alt">
          <button
            type="button"
            className="sign-in__link"
            onClick={() => {
              setResetEmail(email);
              setShowForgot(true);
            }}
            disabled={status === "signing-in" || status === "sending-link"}
          >
            Forgot password?
          </button>
        </div>
      </div>
    </main>
  );
}
